import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQSpacer from '../../components/spacer';
import UQCard from '../../components/card';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import UQTooltip from '../../components/tooltip';
import UQButton from '../../components/button';
import UQIcon from '../../components/icon';
import { BREADCRUMB_ROOT } from '../../common/config';

const TooltipDemoPage: React.FC = () => {
  const attributes = [
    {
      attribute: 'children',
      type: 'React.ReactNode',
      default: '-',
      description: 'The element that triggers the tooltip',
    },
    {
      attribute: 'content',
      type: 'React.ReactNode',
      default: '-',
      description: 'The content to display in the tooltip',
    },
    {
      attribute: 'position',
      type: "'top' | 'bottom' | 'left' | 'right'",
      default: "'top'",
      description: 'Preferred position of the tooltip',
    },
    {
      attribute: 'disabled',
      type: 'boolean',
      default: 'false',
      description: 'Whether the tooltip is disabled',
    },
    {
      attribute: 'delay',
      type: 'number',
      default: '500',
      description: 'Delay in milliseconds before showing tooltip',
    },
    {
      attribute: 'className',
      type: 'string',
      default: "''",
      description: 'Additional CSS class name',
    },
    {
      attribute: 'maxWidth',
      type: 'number',
      default: '250',
      description: 'Maximum width of the tooltip in pixels',
    },
    {
      attribute: 'maxHeight',
      type: 'number',
      default: '300',
      description: 'Maximum height of the tooltip in pixels, enables scrolling',
    },
    {
      attribute: 'usePortal',
      type: 'boolean',
      default: 'false',
      description: 'Render tooltip in a portal to avoid clipping',
    },
    {
      attribute: 'interactive',
      type: 'boolean',
      default: 'false',
      description: 'Allow hovering over tooltip to keep it visible',
    },
    {
      attribute: 'hideDelay',
      type: 'number',
      default: '300',
      description: 'Delay in milliseconds before hiding tooltip',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const basicExample = `
import React from 'react';
import { UQTooltip, UQButton } from '@uniqconsulting/ui';

<UQTooltip content="This tooltip appears on top" position="top">
  <UQButton type="primary">Top Tooltip</UQButton>
</UQTooltip>
`;

  const interactiveExample = `
import React from 'react';
import { UQTooltip, UQButton } from '@uniqconsulting/ui';

<UQTooltip 
  content="This tooltip is interactive - you can hover over it!"
  interactive={true}
  hideDelay={500}
  usePortal={true}
>
  <UQButton type="primary">Interactive Tooltip</UQButton>
</UQTooltip>
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Tooltip']} />
      <h1>UQTooltip Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQTooltip</code> component provides contextual information when users hover over elements. 
        It supports multiple positions, custom delays, interactive content, and portal rendering to avoid clipping issues.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
        rowLines
      />
      <UQSpacer size="m" />

      {/* Live Demo */}
      <h2>Live Demo</h2>
      
      <h3>Position Examples</h3>
      <UQSpacer size="s" />
      <UQCard>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center', 
          flexWrap: 'wrap'
        }}>
          <UQTooltip content="This tooltip appears on top" position="top">
            <UQButton type="primary">Top Tooltip</UQButton>
          </UQTooltip>
          
          <UQTooltip content="This tooltip appears on the bottom" position="bottom">
            <UQButton type="primary">Bottom Tooltip</UQButton>
          </UQTooltip>
          
          <UQTooltip content="This tooltip appears on the left" position="left">
            <UQButton type="primary">Left Tooltip</UQButton>
          </UQTooltip>
          
          <UQTooltip content="This tooltip appears on the right" position="right">
            <UQButton type="primary">Right Tooltip</UQButton>
          </UQTooltip>
        </div>
      </UQCard>
      <UQSpacer size="s" />
      <UQCode code={basicExample} enableCopy />
      <UQSpacer size="m" />

      <h3>Icon with Tooltip</h3>
      <UQSpacer size="s" />
      <UQCard>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center'
        }}>
          <UQTooltip content="Information icon - provides helpful context">
            <UQIcon icon="annotation-information" style={{ width: '24px', height: '24px' }} />
          </UQTooltip>
          
          <UQTooltip content="Alert - something needs attention!">
            <UQIcon icon="alert-triangle" style={{ width: '24px', height: '24px' }} />
          </UQTooltip>
          
          <UQTooltip content="Settings configuration">
            <UQIcon icon="settings-01" style={{ width: '24px', height: '24px' }} />
          </UQTooltip>
        </div>
      </UQCard>
      <UQSpacer size="m" />

      <h3>Custom Delay and Max Width</h3>
      <UQSpacer size="s" />
      <UQCard>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center', 
          flexWrap: 'wrap'
        }}>
          <UQTooltip 
            content="This tooltip has a longer delay (1000ms)" 
            delay={1000}
          >
            <UQButton type="secondary">Slow Tooltip</UQButton>
          </UQTooltip>
          
          <UQTooltip 
            content="This is a really long tooltip content that demonstrates the maxWidth property working correctly and wrapping text when needed"
            maxWidth={200}
          >
            <UQButton type="action">Long Content</UQButton>
          </UQTooltip>
        </div>
      </UQCard>
      <UQSpacer size="m" />

      <h3>Interactive Features</h3>
      <UQSpacer size="s" />
      <UQCard>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center', 
          flexWrap: 'wrap'
        }}>
          <UQTooltip 
            content="This tooltip is interactive - you can hover over it and it will stay visible! Try moving your mouse from the button to the tooltip."
            interactive={true}
            hideDelay={500}
            usePortal={true}
          >
            <UQButton type="primary">Interactive Tooltip</UQButton>
          </UQTooltip>
          
          <UQTooltip 
            content="This is a really long tooltip content that demonstrates the maxHeight property with scrollable content. When the content exceeds the maximum height, a scrollbar will appear."
            maxHeight={150}
            interactive={true}
            usePortal={true}
            hideDelay={300}
          >
            <UQButton type="secondary">Scrollable Content</UQButton>
          </UQTooltip>
          
          <UQTooltip 
            content="Portal tooltip that renders outside the container hierarchy to avoid z-index and overflow issues"
            usePortal={true}
            hideDelay={200}
          >
            <UQButton type="action">Portal Tooltip</UQButton>
          </UQTooltip>
        </div>
      </UQCard>
      <UQSpacer size="s" />
      <UQCode code={interactiveExample} enableCopy />
      <UQSpacer size="m" />

      <h3>Rich Content and Disabled State</h3>
      <UQSpacer size="s" />
      <UQCard>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center'
        }}>
          <UQTooltip content={
            <div>
              <strong>Rich Content</strong><br />
              You can use HTML elements in tooltips
            </div>
          }>
            <span style={{ 
              padding: '0.5rem 1rem', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '4px',
              cursor: 'help'
            }}>
              Hover for rich content
            </span>
          </UQTooltip>
          
          <UQTooltip content="Disabled tooltip example" disabled>
            <UQButton type="primary" disabled>Disabled Tooltip</UQButton>
          </UQTooltip>
        </div>
      </UQCard>
    </>
  );
};

export default TooltipDemoPage;
